import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import { financeTransactions, bankList, getCarryOver, cashAuditLogs, currentUser, type FinanceTransaction, type CurrencyCode, type PaymentSplit, currencyList, currencySymbols, expenseCategories } from '@/mocks/cash';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import AuditLogPanel from '../components/AuditLogPanel';

interface Toast { id: number; message: string; type: 'success' | 'error'; }

export default function NakitKasaPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [txList, setTxList] = useState<FinanceTransaction[]>(financeTransactions);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTx, setEditTx] = useState<FinanceTransaction | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [deleteConfirmTx, setDeleteConfirmTx] = useState<FinanceTransaction | null>(null);
  const [showAuditLog, setShowAuditLog] = useState(false);

  const [formDate, setFormDate] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCustomer, setFormCustomer] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formType, setFormType] = useState<'income'|'expense'>('income');
  const [formPayment, setFormPayment] = useState('Nakit');
  const [formExpenseCategory, setFormExpenseCategory] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formCurrency, setFormCurrency] = useState<CurrencyCode>('TRY');
  const [enableSplit, setEnableSplit] = useState(false);
  const [splits, setSplits] = useState<PaymentSplit[]>([{ amount: 0, currency: 'TRY', method: 'Nakit', notes: '' }]);

  const todayStr = '2026-05-09';

  const dateRange = useMemo(() => {
    if (dateFilter === 'today') return { start: todayStr, end: todayStr, label: 'Bugün' };
    if (dateFilter === 'yesterday') return { start: '2026-05-08', end: '2026-05-08', label: 'Dün' };
    if (dateFilter === 'week') return { start: '2026-05-03', end: todayStr, label: 'Bu Hafta' };
    if (dateFilter === 'month') return { start: '2026-05-01', end: todayStr, label: 'Bu Ay' };
    if (dateFilter === 'custom' && customStart && customEnd) return { start: customStart, end: customEnd, label: `${customStart} — ${customEnd}` };
    return { start: '', end: '', label: 'Tüm Zamanlar' };
  }, [dateFilter, customStart, customEnd]);

  const filtered = useMemo(() => {
    let data = txList.filter(t => t.account === 'cash');
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(t =>
        (t.customerName?.toLowerCase().includes(q) || '') ||
        t.description.toLowerCase().includes(q)
      );
    }
    if (dateFilter === 'today') data = data.filter(t => t.date === todayStr);
    if (dateFilter === 'yesterday') data = data.filter(t => t.date === '2026-05-08');
    if (dateFilter === 'week') data = data.filter(t => t.date >= '2026-05-03');
    if (dateFilter === 'month') data = data.filter(t => t.date >= '2026-05-01');
    if (dateFilter === 'custom' && customStart && customEnd) data = data.filter(t => t.date >= customStart && t.date <= customEnd);
    return data.sort((a, b) => `${b.date}T${b.time}`.localeCompare(`${a.date}T${a.time}`));
  }, [txList, searchQuery, dateFilter, customStart, customEnd]);

  const stats = useMemo(() => {
    const start = dateRange.start || '1900-01-01';
    const end = dateRange.end || '2099-12-31';
    const txs = txList.filter(t => t.account === 'cash' && t.date >= start && t.date <= end);
    const opening = getCarryOver(start, 'cash');
    const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { opening, income, expense, closing: opening + income - expense, count: txs.length };
  }, [txList, dateRange]);

  const multiCurrencyStats = useMemo(() => {
    const start = dateRange.start || '1900-01-01';
    const end = dateRange.end || '2099-12-31';
    const allTxs = txList.filter(t => t.account === 'cash' && t.date >= start && t.date <= end);
    const currencies: CurrencyCode[] = ['TRY', 'EUR', 'USD'];
    return currencies.map(curr => {
      const txs = allTxs.filter(t => (t.currency || 'TRY') === curr);
      const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
      const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
      const opening = curr === 'TRY' ? getCarryOver(start, 'cash') : 0;
      return { currency: curr, symbol: currencySymbols[curr], opening, income, expense, closing: opening + income - expense };
    });
  }, [txList, dateRange]);

  const dailyBreakdown = useMemo(() => {
    const days = Array.from(new Set(filtered.map(t => t.date))).sort().reverse();
    return days.map(date => {
      const dayTxs = filtered.filter(t => t.date === date);
      const inc = dayTxs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
      const exp = dayTxs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
      const opening = getCarryOver(date, 'cash');
      return { date, opening, income: inc, expense: exp, closing: opening + inc - exp, count: dayTxs.length };
    });
  }, [filtered]);

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const openModal = (tx?: FinanceTransaction) => {
    if (tx) {
      setEditTx(tx);
      setFormDate(tx.date);
      setFormTime(tx.time);
      setFormDesc(tx.description);
      setFormCustomer(tx.customerName || '');
      setFormAmount(tx.amount.toString());
      setFormType(tx.type);
      setFormCurrency(tx.currency || 'TRY');
      setFormPayment(tx.paymentType);
      setFormExpenseCategory(tx.expenseCategory || '');
      setFormNotes(tx.notes);
      // Editing: always non-split (splits already routed to separate txs)
      setEnableSplit(false);
      setSplits([{ amount: tx.amount, currency: tx.currency || 'TRY', method: tx.paymentType as PaymentSplit['method'], notes: tx.notes || '' }]);
    } else {
      setEditTx(null);
      setFormDate(todayStr);
      setFormTime(new Date().toTimeString().slice(0, 5));
      setFormDesc('');
      setFormCustomer('');
      setFormAmount('');
      setFormType('income');
      setFormCurrency('TRY');
      setFormPayment('Nakit');
      setFormExpenseCategory('');
      setFormNotes('');
      setEnableSplit(false);
      setSplits([{ amount: 0, currency: 'TRY', method: 'Nakit', notes: '' }]);
    }
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditTx(null); };

  const handleSave = () => {
    if (!formDate || !formDesc.trim()) {
      showToast('Lütfen tarih ve açıklama alanlarını doldurun.', 'error');
      return;
    }
    if (formType === 'expense' && !formExpenseCategory) {
      showToast('Lütfen gider kalemi seçin.', 'error');
      return;
    }
    const totalSplits = enableSplit ? splits.reduce((s, sp) => s + sp.amount, 0) : Number(formAmount || 0);
    if (totalSplits <= 0) {
      showToast('Tutar 0\'dan büyük olmalıdır.', 'error');
      return;
    }

    // --- Parçalı ödeme: her parça için ayrı transaction oluştur ---
    if (enableSplit && !editTx) {
      const groupId = `split-${Date.now()}`;
      const newTxs: FinanceTransaction[] = splits
        .filter(sp => sp.amount > 0)
        .map((sp, i) => {
          const isCash = sp.method === 'Nakit';
          return {
            id: `fin-${Date.now()}-${i}`,
            date: formDate,
            time: formTime || '00:00',
            description: formDesc.trim(),
            customerName: formCustomer.trim() || undefined,
            amount: sp.amount,
            currency: sp.currency || formCurrency,
            type: formType,
            account: isCash ? 'cash' as const : 'bank' as const,
            bankName: isCash ? undefined : (sp.bankName || undefined),
            paymentType: sp.method as FinanceTransaction['paymentType'],
            notes: sp.notes || formNotes.trim(),
            expenseCategory: formType === 'expense' && formExpenseCategory ? formExpenseCategory : undefined,
            splitGroupId: groupId,
          };
        });
      newTxs.forEach(tx => financeTransactions.unshift(tx));
      setTxList(prev => [...newTxs, ...prev]);
      showToast(`Parçalı ödeme kaydedildi: ${newTxs.length} ayrı hesaba yazıldı.`, 'success');
      closeModal();
      return;
    }

    const payload: FinanceTransaction = {
      id: editTx ? editTx.id : `fin-${Date.now()}`,
      date: formDate,
      time: formTime || '00:00',
      description: formDesc.trim(),
      customerName: formCustomer.trim() || undefined,
      amount: Number(formAmount),
      currency: formCurrency,
      type: formType,
      account: 'cash',
      paymentType: formPayment as FinanceTransaction['paymentType'],
      notes: formNotes.trim(),
      expenseCategory: formType === 'expense' && formExpenseCategory ? formExpenseCategory : undefined,
    };

    if (editTx) {
      // Audit log
      cashAuditLogs.unshift({
        id: `log-${Date.now()}`,
        action: 'edit',
        timestamp: new Date().toISOString(),
        performedBy: currentUser,
        txSnapshot: { ...payload },
        previousSnapshot: { ...editTx },
      });
      const idx = financeTransactions.findIndex(t => t.id === editTx.id);
      if (idx > -1) financeTransactions[idx] = payload;
      setTxList(prev => prev.map(t => t.id === editTx.id ? payload : t));
      showToast('İşlem güncellendi ve loglandı.', 'success');
    } else {
      financeTransactions.unshift(payload);
      setTxList(prev => [payload, ...prev]);
      showToast('Yeni işlem eklendi.', 'success');
    }
    closeModal();
  };

  const handleDeleteRequest = (tx: FinanceTransaction) => {
    setDeleteConfirmTx(tx);
  };

  const handleDeleteConfirmed = (reason: string) => {
    if (!deleteConfirmTx) return;
    const idx = financeTransactions.findIndex(t => t.id === deleteConfirmTx.id);
    if (idx > -1) financeTransactions.splice(idx, 1);
    setTxList(prev => prev.filter(t => t.id !== deleteConfirmTx.id));
    showToast('İşlem silindi ve loglandı.', 'success');
    setDeleteConfirmTx(null);
  };

  const splitTotal = useMemo(() => splits.reduce((s, sp) => s + (Number(sp.amount) || 0), 0), [splits]);

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 animate-slide-in" style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/kasa')} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-arrow-left-line" style={{ color: 'var(--text-muted)' }} />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Nakit Kasa</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{dateRange.label} — {filtered.length} hareket</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAuditLog(true)}
            className="spa-btn-secondary text-sm flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-history-line text-sm" /></div>
            Audit Log
          </button>
          <button onClick={() => openModal()} className="spa-btn-primary text-sm flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Yeni İşlem
          </button>
        </div>
      </div>

      {/* Multi-Currency Summary Table */}
      <div className="spa-card overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-money-cny-circle-line text-sm" style={{ color: '#C9A962' }} /></div>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Kasa Özeti</span>
          <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>{dateRange.label}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                <th className="px-4 py-2.5 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)', minWidth: 100 }}>Para Birimi</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold whitespace-nowrap" style={{ color: '#8B5CF6' }}>Devir Bakiye</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold whitespace-nowrap" style={{ color: '#10B981' }}>Toplam Gelir</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold whitespace-nowrap" style={{ color: '#EF4444' }}>Toplam Gider</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold whitespace-nowrap" style={{ color: '#2D5F3E' }}>Kapanış Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {multiCurrencyStats.map((row, i) => (
                <tr key={row.currency} className="border-b transition-colors hover:bg-theme-hover" style={{ borderColor: 'var(--border-color)', background: i % 2 === 1 ? 'var(--bg-tertiary)' : 'transparent' }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{
                        background: row.currency === 'TRY' ? 'rgba(201,169,98,0.15)' : row.currency === 'EUR' ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.15)',
                        color: row.currency === 'TRY' ? '#C9A962' : row.currency === 'EUR' ? '#10B981' : '#3B82F6'
                      }}>{row.currency}</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                        {row.symbol} {row.currency === 'TRY' ? 'Türk Lirası' : row.currency === 'EUR' ? 'Euro' : 'Dolar'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
                      {row.symbol}{row.opening.toLocaleString('tr-TR', { minimumFractionDigits: row.currency === 'TRY' ? 0 : 2, maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold font-display" style={{ color: '#10B981' }}>
                      +{row.symbol}{row.income.toLocaleString('tr-TR', { minimumFractionDigits: row.currency === 'TRY' ? 0 : 2, maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold font-display" style={{ color: '#EF4444' }}>
                      {row.expense > 0 ? `-${row.symbol}${row.expense.toLocaleString('tr-TR', { minimumFractionDigits: row.currency === 'TRY' ? 0 : 2, maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}` : `${row.symbol}0`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold font-display" style={{ color: row.closing >= 0 ? '#2D5F3E' : '#EF4444' }}>
                      {row.symbol}{row.closing.toLocaleString('tr-TR', { minimumFractionDigits: row.currency === 'TRY' ? 0 : 2, maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
          {[
            { key: 'today', label: 'Bugün' },
            { key: 'yesterday', label: 'Dün' },
            { key: 'week', label: 'Bu Hafta' },
            { key: 'month', label: 'Bu Ay' },
            { key: 'all', label: 'Tümü' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => { setDateFilter(f.key); setShowCustom(false); }}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all"
              style={{
                background: dateFilter === f.key
                  ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                  : 'transparent',
                color: dateFilter === f.key ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => { setDateFilter('custom'); setShowCustom(true); }}
            className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all"
            style={{
              background: dateFilter === 'custom'
                ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                : 'transparent',
              color: dateFilter === 'custom' ? '#fff' : 'var(--text-secondary)',
            }}
          >
            Özel
          </button>
        </div>
        {showCustom && (
          <div className="flex gap-2 items-center">
            <input type="date" className="spa-input text-sm py-1.5" value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>—</span>
            <input type="date" className="spa-input text-sm py-1.5" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
            <button onClick={() => setDateFilter('custom')} className="spa-btn-primary text-xs px-3 py-1.5">Uygula</button>
          </div>
        )}
        <div className="relative flex-1 max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10 text-sm" placeholder="Ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {/* Daily Breakdown */}
      {dailyBreakdown.length > 0 && dateFilter !== 'all' && (
        <div className="spa-card p-4">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Gün Sonu Özeti</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  {['Tarih','Devir','Gelir','Gider','Kapanış','Hareket'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {dailyBreakdown.map(d => (
                  <tr key={d.date} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{d.date}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>₺{d.opening.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: '#10B981' }}>₺{d.income.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: '#EF4444' }}>₺{d.expense.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs font-bold font-display" style={{ color: '#2D5F3E' }}>₺{d.closing.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className="spa-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Tarih','Saat','Açıklama','Müşteri','Tür','Kasaya Giren','Hizmet Bedeli','Gider Kalemi','Notlar','İşlemler'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(tx => [
                <tr key={`${tx.id}-main`} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>{tx.date}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>{tx.time}</td>
                  <td className="px-3 py-3" style={{ color: 'var(--text-primary)' }}>{tx.description}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>{tx.customerName || '-'}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><StatusBadge status={tx.type === 'income' ? 'Kapalı' : 'Açık'}>{tx.type === 'income' ? 'Gelir' : 'Gider'}</StatusBadge></td>
                  <td className="px-3 py-3 whitespace-nowrap font-semibold font-display" style={{ color: tx.type === 'income' ? '#10B981' : '#EF4444' }}>
                    {tx.type === 'income' ? '+' : '-'}{currencySymbols[tx.currency || 'TRY']}{tx.amount.toLocaleString('tr-TR')}
                    {tx.currency !== 'TRY' && <span className="text-[10px] ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>{tx.currency}</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {tx.serviceAmount && tx.serviceAmount !== tx.amount ? (
                      <span className="font-medium font-display text-xs" style={{ color: 'var(--text-primary)' }}>
                        {currencySymbols[tx.serviceCurrency || 'TRY']}{tx.serviceAmount.toLocaleString('tr-TR')}
                        {tx.serviceCurrency && tx.serviceCurrency !== 'TRY' && <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>{tx.serviceCurrency}</span>}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {tx.type === 'expense' && tx.expenseCategory ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
                        <i className="ri-price-tag-3-line" />
                        {tx.expenseCategory}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs max-w-[200px] truncate" style={{ color: 'var(--text-muted)' }}>{tx.notes || '-'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openModal(tx)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)' }} title="Düzenle"><i className="ri-pencil-line text-xs" style={{ color: '#3B82F6' }} /></button>
                      <button onClick={() => handleDeleteRequest(tx)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil"><i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} /></button>
                    </div>
                  </td>
                </tr>,
                tx.paymentSplits && tx.paymentSplits.length > 0 && (
                  <tr key={`${tx.id}-splits`} style={{ background: 'var(--bg-tertiary)' }}>
                    <td colSpan={10} className="px-3 py-2">
                      <div className="flex gap-2 flex-wrap">
                        {tx.paymentSplits.map((sp, si) => (
                          <div key={si} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                            <span className="font-medium">{currencySymbols[sp.currency || 'TRY']}{sp.amount.toLocaleString('tr-TR')}</span>
                            <span style={{ color: 'var(--text-muted)' }}>· {sp.method}</span>
                            {sp.bankName && <span style={{ color: 'var(--text-muted)' }}>· {sp.bankName}</span>}
                            {sp.notes && <span style={{ color: 'var(--text-muted)' }}>· {sp.notes}</span>}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )
              ])}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-money-cny-circle-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>İşlem bulunamadı</p>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmTx && (
        <DeleteConfirmModal
          tx={deleteConfirmTx}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeleteConfirmTx(null)}
        />
      )}

      {/* Audit Log Panel */}
      {showAuditLog && <AuditLogPanel onClose={() => setShowAuditLog(false)} />}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeModal}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{editTx ? 'İşlemi Düzenle' : 'Yeni Nakit İşlemi'}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                <button onClick={() => setFormType('income')} className="flex-1 py-2 rounded-md text-sm font-medium cursor-pointer transition-all" style={{ background: formType === 'income' ? '#10B981' : 'transparent', color: formType === 'income' ? '#fff' : 'var(--text-secondary)' }}>Gelir</button>
                <button onClick={() => setFormType('expense')} className="flex-1 py-2 rounded-md text-sm font-medium cursor-pointer transition-all" style={{ background: formType === 'expense' ? '#EF4444' : 'transparent', color: formType === 'expense' ? '#fff' : 'var(--text-secondary)' }}>Gider</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tarih <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="date" className="spa-input text-sm" value={formDate} onChange={e => setFormDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Saat <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="time" className="spa-input text-sm" value={formTime} onChange={e => setFormTime(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Açıklama <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Örn. Klasik Masaj" value={formDesc} onChange={e => setFormDesc(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Müşteri</label>
                  <input className="spa-input text-sm" placeholder="Müşteri adı (opsiyonel)" value={formCustomer} onChange={e => setFormCustomer(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Para Birimi</label>
                  <select className="spa-input text-sm" value={formCurrency} onChange={e => {
                    setFormCurrency(e.target.value as CurrencyCode);
                    setSplits(prev => prev.map(s => ({ ...s, currency: e.target.value as CurrencyCode })));
                  }}>
                    {currencyList.map(c => <option key={c} value={c}>{c} ({currencySymbols[c]})</option>)}
                  </select>
                </div>
              </div>
              {!enableSplit && (
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tutar ({currencySymbols[formCurrency]}) <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={formAmount} onChange={e => setFormAmount(e.target.value)} />
                </div>
              )}
              {enableSplit && (
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Toplam Tutar ({currencySymbols[formCurrency]})</label>
                  <div className="flex items-center gap-2">
                    <input type="number" className="spa-input text-sm bg-amber-50/50" readOnly value={splitTotal} />
                    <span className="text-[10px] px-2 py-1 rounded-md font-medium" style={{ background: 'rgba(201,169,98,0.15)', color: '#C9A962' }}>Parçalı</span>
                  </div>
                </div>
              )}

              {/* Split Payment Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg cursor-pointer" style={{ background: 'var(--bg-tertiary)' }} onClick={() => setEnableSplit(!enableSplit)}>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-funds-line" style={{ color: 'var(--text-secondary)' }} /></div>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Parçalı Ödeme</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{enableSplit ? 'Açık' : 'Kapalı'}</span>
                  <div className="w-10 h-5 rounded-full relative transition-all" style={{ background: enableSplit ? '#10B981' : 'var(--border-color)' }}>
                    <div className="w-4 h-4 rounded-full absolute top-0.5 transition-all" style={{ background: '#fff', left: enableSplit ? '22px' : '2px' }} />
                  </div>
                </div>
              </div>

              {/* Split Payment Rows */}
              {enableSplit && (
                <div className="space-y-3">
                  {splits.map((split, idx) => (
                    <div key={idx} className="p-3 rounded-lg border space-y-2" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Parça {idx + 1}</span>
                        {splits.length > 1 && (
                          <button onClick={() => setSplits(prev => prev.filter((_, i) => i !== idx))} className="w-5 h-5 flex items-center justify-center cursor-pointer" style={{ color: '#EF4444' }}>
                            <i className="ri-close-line text-xs" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Tutar ({currencySymbols[split.currency]})</label>
                          <input type="number" className="spa-input text-sm py-1.5" placeholder="0" value={split.amount || ''} onChange={e => {
                            const val = Number(e.target.value);
                            setSplits(prev => prev.map((s, i) => i === idx ? { ...s, amount: val } : s));
                          }} />
                        </div>
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Ödeme Yöntemi</label>
                          <select className="spa-input text-sm py-1.5" value={split.method} onChange={e => setSplits(prev => prev.map((s, i) => i === idx ? { ...s, method: e.target.value } : s))}>
                            <option value="Nakit">Nakit</option>
                            <option value="Kredi Kartı">Kredi Kartı</option>
                            <option value="Havale">Havale</option>
                            <option value="Kredi Kartı (Yabancı)">Kredi Kartı (Yabancı)</option>
                          </select>
                        </div>
                      </div>
                      {split.method === 'Havale' && (
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Banka</label>
                          <select className="spa-input text-sm py-1.5" value={split.bankName || ''} onChange={e => setSplits(prev => prev.map((s, i) => i === idx ? { ...s, bankName: e.target.value } : s))}>
                            <option value="">Banka Seçin</option>
                            {bankList.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Notlar</label>
                        <input className="spa-input text-sm py-1.5" placeholder="Parça için not..." value={split.notes || ''} onChange={e => setSplits(prev => prev.map((s, i) => i === idx ? { ...s, notes: e.target.value } : s))} />
                      </div>
                    </div>
                  ))}
                  <button onClick={() => setSplits(prev => [...prev, { amount: 0, currency: formCurrency, method: 'Nakit', notes: '' }])} className="w-full py-2 rounded-lg text-xs font-medium cursor-pointer border border-dashed" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
                      Yeni Parça Ekle
                    </div>
                  </button>
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Parça Toplamı:</span>
                    <span className="text-sm font-bold font-display" style={{ color: splitTotal === Number(formAmount || 0) ? '#10B981' : '#EF4444' }}>
                      {currencySymbols[formCurrency]}{splitTotal.toLocaleString('tr-TR')}
                      {Number(formAmount) > 0 && (
                        <span className="text-[10px] font-normal ml-1" style={{ color: 'var(--text-muted)' }}>/ {currencySymbols[formCurrency]}{Number(formAmount).toLocaleString('tr-TR')}</span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {!enableSplit && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Ödeme Tipi</label>
                    <select
                      className="spa-input text-sm"
                      value={formPayment}
                      onChange={e => setFormPayment(e.target.value)}
                    >
                      <option value="Nakit">Nakit</option>
                      <option value="Kredi Kartı">Kredi Kartı</option>
                      <option value="Havale">Havale</option>
                      <option value="Açık Ödeme">Açık Ödeme</option>
                      <option value="Eksik Ödeme">Eksik Ödeme</option>
                      <option value="Devir">Devir</option>
                      <option value="Gider">Gider</option>
                    </select>
                  </div>
                  {formType === 'expense' && (
                    <div>
                      <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Gider Kalemi <span style={{ color: '#EF4444' }}>*</span></label>
                      <select className="spa-input text-sm" value={formExpenseCategory} onChange={e => setFormExpenseCategory(e.target.value)}>
                        <option value="">Kalem Seçin</option>
                        {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
                <textarea className="spa-input text-sm min-h-[80px] resize-none" placeholder="Ek notlar..." maxLength={500} value={formNotes} onChange={e => setFormNotes(e.target.value)} />
                <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{formNotes.length}/500</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleSave} className="spa-btn-primary text-sm">{editTx ? 'Güncelle' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}